import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Copy, Crown, Gem, RotateCcw, Scale, X, Loader } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useAppStore from '../../../store/useAppStore'
import useRoyaleStore from '../store/useRoyaleStore'
import RoyaleBetsFeed from '../components/RoyaleBetsFeed'
import RoyaleParticipantsShowcase from '../components/RoyaleParticipantsShowcase'
import RoyaleTeamsSummary from '../components/RoyaleTeamsSummary'
import RoyaleWinnerCard from '../components/RoyaleWinnerCard'
import RoyaleUserShare from '../components/RoyaleUserShare'

const RoyaleRound = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()
  const { loadRoundById } = useRoyaleStore()
  const { user } = useAppStore()

  const [round, setRound] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isFairPlayOpen, setIsFairPlayOpen] = useState(false)

  useEffect(() => {
    loadRound()
  }, [id])

  const loadRound = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await loadRoundById(id)
      setRound(data)
    } catch (err) {
      console.error('Ошибка загрузки раунда Royale:', err)
      setError(err.message || t('royale.round.loadError'))
      toast.error(t('royale.round.loadErrorToast'))
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
        <div className="mx-auto flex min-h-[60vh] max-w-7xl items-center justify-center">
          <Loader className="w-8 h-8 text-accent-primary animate-spin" />
        </div>
      </div>
    )
  }

  if (!round && error) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  const winner = round?.winner
  const users = round?.users || []
  // Ключа `teams` может не быть вовсе — банд в раунде не было
  const teams = round?.teams || []
  const totalBetValue = Number(round?.total_bet ?? round?.total_crystal ?? 0)
  const roundStatus = round?.status || 'finished'
  const statusLabels = {
    new: t('royale.status.new'),
    waiting: t('royale.status.waiting'),
    raffle: t('royale.status.raffle'),
    result: t('royale.status.result'),
    finished: t('royale.status.finished')
  }
  const statusLabel = statusLabels[roundStatus] || t('royale.status.finished')
  const statusClassName = roundStatus === 'waiting'
    ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
    : 'bg-white/10 text-white/80 border border-white/10'
  const winnerPercent = winner?.percent
    ?? users.find((user) => String(user.chat_id || user.id) === String(winner?.chat_id || winner?.id))?.percent
    ?? (totalBetValue > 0 && winner?.total_crystal ? (Number(winner.total_crystal) / totalBetValue) * 100 : null)
  const winnerAmount = totalBetValue
  const highlightId = String(winner?.chat_id || winner?.id || winner?.name || '')
  // Индекс победителя в users — надёжнее, чем строковый id, когда у участника пустые name/chat_id/id
  const highlightIndex = users.findIndex((u) => u?.win)
  const currentUserId = String(user?.chat_id || user?.id || '')
  const isParticipant = (round?.bets || []).some((bet) => bet.your_bet
    || (currentUserId && String(bet.chat_id || bet.id) === currentUserId))
  const isCurrentUser = (round?.bets || []).some((bet) => bet.win_bet
    && (bet.your_bet || (currentUserId && String(bet.chat_id || bet.id) === currentUserId)))
    || Boolean(winner?.you)
    || (Boolean(currentUserId) && String(winner?.chat_id || winner?.id) === currentUserId)
  const userBets = (round?.bets || []).filter((bet) =>
    bet.your_bet || (currentUserId && String(bet.chat_id || '') === currentUserId)
  )
  const userBetTotal = userBets.reduce((sum, bet) => sum + Number(bet.total_crystal || 0), 0)
  // Возврат как тиммейту победителя: ставок у игрока может быть несколько
  const userRefund = userBets.reduce((sum, bet) => sum + Number(bet.refund || 0), 0)
    || Number(users.find((participant) => String(participant.chat_id || '') === currentUserId)?.refund || 0)
  const refundToTeammates = Number(round?.sent_win?.refund_crystal ?? round?.refund_crystal ?? 0)
  const userSharePercent = totalBetValue > 0 ? (userBetTotal / totalBetValue) * 100 : null
  const userDisplaySource = userBets[0]
    || users.find((participant) => String(participant.chat_id || participant.id || '') === currentUserId)
  const userAvatar = userDisplaySource?.ava
  const userName = userDisplaySource?.name || user?.username || user?.name || ''
  const fairPlayItems = [
    { label: t('royale.fairPlay.roundId'), value: round?.id },
    { label: t('royale.fairPlay.hash'), value: round?.hash },
    { label: t('royale.fairPlay.roundNumber'), value: round?.number },
    { label: t('royale.fairPlay.salt'), value: round?.salt },
    { label: t('royale.fairPlay.checkHash'), value: round?.check_hash },
    { label: t('royale.fairPlay.checkNumber'), value: round?.check_number }
  ]
  const showStats = users.length > 0

  const formatValue = (value, head = 6, tail = 6) => {
    if (!value) return '—'
    const text = String(value)
    if (text.length <= head + tail + 3) return text
    return `${text.slice(0, head)}...${text.slice(-tail)}`
  }

  const handleCopy = async (label, value) => {
    if (!value) {
      toast.error(t('royale.toasts.copyNoData'))
      return
    }

    try {
      await navigator.clipboard.writeText(String(value))
      toast.success(t('royale.toasts.copied', { label }))
    } catch (err) {
      console.error('Ошибка копирования:', err)
      toast.error(t('royale.toasts.copyFailed'))
    }
  }

  const actionButtons = (
    <>
      {(round?.hash || round?.number || round?.salt) && (
        <button
          type="button"
          onClick={() => setIsFairPlayOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
        >
          <Scale className="w-4 h-4" />
          <span className="text-sm">{t('royale.buttons.fairPlay')}</span>
        </button>
      )}
    </>
  )

  return (
    <div className="px-4 py-6 pb-5 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="space-y-4 lg:space-y-6">
          <div className="relative flex items-center justify-center lg:min-h-[48px]">
            <button
              onClick={() => navigate('/royale/history')}
              className="absolute left-0 p-2 hover:bg-surface rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>

            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-accent-primary lg:w-8 lg:h-8" />
              <h1 className="text-2xl font-bold text-white lg:text-4xl">{t('royale.round.title')}</h1>
            </div>
          </div>

          {showStats && (
            <div className="flex items-center justify-between gap-4 lg:hidden">
              <div>
                <p className="text-text-secondary text-xs uppercase tracking-[0.2em]">{t('royale.bank')}</p>
                <p className="mt-2 text-3xl font-bold text-accent-primary inline-flex items-center gap-2">
                  <Gem className="w-6 h-6 text-accent-primary" />
                  {totalBetValue.toLocaleString()}
                </p>
              </div>

              <div className="text-right">
                <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${statusClassName}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
          )}
        </div>

        {round && (
          <>
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-6">
              <div className="space-y-6 lg:space-y-8">
                {winner && (
                  <RoyaleWinnerCard
                    winner={winner}
                    winTicket={round?.win_ticket}
                    winAmount={winnerAmount}
                    winPercent={winnerPercent}
                    isCurrentUser={isCurrentUser}
                    isParticipant={isParticipant}
                    refundAmount={userRefund}
                    skipEffects
                  />
                )}

                <RoyaleParticipantsShowcase
                  users={users}
                  teams={teams}
                  totalBet={totalBetValue}
                  monochrome
                  highlightId={highlightId}
                  highlightIndex={highlightIndex}
                  containerClassName="w-full aspect-square lg:aspect-[1.35/1]"
                />

                <div className="hidden lg:flex flex-wrap items-center justify-center gap-3">
                  {actionButtons}
                </div>
              </div>

              <div className="mt-6 space-y-6 lg:mt-0 lg:sticky lg:top-28">
                {showStats && (
                  <div className="hidden lg:block pt-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-text-secondary text-xs uppercase tracking-[0.2em]">{t('royale.bank')}</p>
                        <p className="mt-2 text-2xl font-bold text-accent-primary inline-flex items-center gap-2 min-w-0">
                          <Gem className="w-5 h-5 text-accent-primary shrink-0" />
                          {totalBetValue.toLocaleString()}
                        </p>
                      </div>

                      <div className="min-w-0 text-right">
                        <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${statusClassName}`}>
                          {statusLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {isParticipant && userBetTotal > 0 && (
                  <RoyaleUserShare
                    avatar={userAvatar}
                    name={userName}
                    percent={userSharePercent}
                  />
                )}

                {round.sent_win && isCurrentUser && (
                  <div className="bg-surface rounded-xl p-4 border border-white/10">
                    <h3 className="text-white font-semibold mb-3">{t('royale.round.winSent')}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-text-secondary">{t('royale.round.commission')}</span>
                        <span className="text-red-400 font-semibold">
                          {Number(round.sent_win.comission_crystal || 0).toLocaleString()}
                        </span>
                      </div>
                      {/* В командном раунде часть банка ушла тиммейтам — показываем, сколько */}
                      {refundToTeammates > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-text-secondary">{t('royale.round.refundToTeammates')}</span>
                          <span className="text-info font-semibold tabular-nums">
                            {refundToTeammates.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-text-secondary">{t('royale.round.winnerReceived')}</span>
                        <span className="text-accent-primary font-semibold">
                          {Number(round.sent_win.user_get_crystal || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Тиммейт победителя: кристаллы вернулись на баланс, скины — в инвентарь теми же предметами */}
                {!isCurrentUser && userRefund > 0 && (
                  <div className="rounded-xl border border-info/30 bg-info/10 p-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-info/15 text-info">
                        <RotateCcw className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white">{t('royale.round.refundReceived')}</p>
                        <p className="text-[11px] text-text-secondary">{t('royale.round.refundSkinsNote')}</p>
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-1 text-lg font-bold tabular-nums text-info">
                        {userRefund.toLocaleString()}
                        <Gem className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                )}

                <RoyaleTeamsSummary teams={teams} />

                <RoyaleBetsFeed
                  bets={round.bets || []}
                  teams={teams}
                  totalBet={totalBetValue}
                  showTicketRange
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-3 lg:hidden">
              {actionButtons}
            </div>
          </>
        )}
      </div>

      {isFairPlayOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-dark-primary rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-white font-semibold">{t('royale.fairPlay.title')}</h3>
              <button
                type="button"
                onClick={() => setIsFairPlayOpen(false)}
                className="p-2 text-text-secondary hover:text-white"
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              {(round?.hash || round?.number || round?.salt) && (
                <div className="">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-text-secondary">{t('royale.fairPlay.description')}</p>
                    </div>
                  </div>
                  <div className="mt-3 space-y-3">
                    {fairPlayItems
                      .filter((item) => item.value)
                      .map((item) => (
                        <div key={item.label} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs text-text-secondary">{item.label}</p>
                            <p className="text-sm text-white font-medium truncate">{formatValue(item.value)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopy(item.label, item.value)}
                            className="inline-flex items-center gap-1 text-xs text-accent-primary hover:text-accent-primary/80 transition-colors"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RoyaleRound
